import { getTriggerDisplayName } from "@loan-crm/shared"
import type { AdminNotificationRow } from "../api/endpoints/notifications"

type NotificationTone = "neutral" | "success" | "warning" | "danger" | "info"

export interface AdminNotificationTarget {
  label: string
  path: string
}

export interface AdminNotificationPresentation {
  categoryLabel: string
  eventLabel: string
  eventTone: NotificationTone
  target: AdminNotificationTarget | null
}

const KEY_LABELS: Record<string, string> = {
  ALERT_BACKUP_FAILURE: "Backup failure",
  ALERT_CRITICAL_SERVICE_DOWN: "Critical service down",
  ALERT_MIGRATION_SPIKE: "Migration spike",
  ALERT_DISBURSEMENT_EXHAUSTED: "Disbursement exhausted",
  NOTIFY_CATEGORY_Z_ENTRY: "Category Z entry",
  NOTIFY_TARGET_SHORTFALL_WARNING: "Target shortfall",
  NOTIFY_UNPROCESSED_TIME_ALERT: "Unprocessed time alert",
  NOTIFY_TICKET_ESCALATION: "Ticket escalation",
  NOTIFY_TICKET_RESPONSE: "Ticket response",
  NOTIFY_KYC_COMPLETION: "KYC completed",
  NOTIFY_KYC_STATUS_UPDATE: "KYC status update",
  NOTIFY_NEW_CLIENT_ASSIGNMENT: "Client assignment",
  NOTIFY_PAYMENT_RECEIVED: "Payment received",
  NOTIFY_LOAN_DISBURSEMENT: "Loan disbursed",
  NOTIFY_LOAN_CLOSURE: "Loan closed",
  NOTIFY_PTP_REMINDER: "Promise to pay reminder",
  NOTIFY_PTP_EXPIRY: "Promise to pay expired",
  NOTIFY_PRE_DUE_REMINDER: "Pre-due reminder",
  NOTIFY_OVERDUE_NOTICE: "Overdue notice",
  NOTIFY_CATEGORY_ESCALATION: "Category escalation",
  NOTIFY_MANUAL_PAYMENT_VERIFIED: "Manual payment verified",
  NOTIFY_MANUAL_PAYMENT_REJECTED: "Manual payment rejected",
  NOTIFY_MICRO_TIER_PROMOTION: "Micro tier promotion",
  NOTIFY_MICRO_TIER_DEMOTION: "Micro tier demotion",
  NOTIFY_MICRO_LOAN_REJECTION: "Micro loan rejection",
}

const TONE_BY_KEY: Record<string, NotificationTone> = {
  ALERT_BACKUP_FAILURE: "danger",
  ALERT_CRITICAL_SERVICE_DOWN: "danger",
  ALERT_MIGRATION_SPIKE: "warning",
  ALERT_DISBURSEMENT_EXHAUSTED: "danger",
  NOTIFY_CATEGORY_Z_ENTRY: "danger",
  NOTIFY_TARGET_SHORTFALL_WARNING: "warning",
  NOTIFY_UNPROCESSED_TIME_ALERT: "warning",
  NOTIFY_TICKET_ESCALATION: "warning",
  NOTIFY_TICKET_RESPONSE: "info",
  NOTIFY_KYC_COMPLETION: "success",
  NOTIFY_KYC_STATUS_UPDATE: "info",
  NOTIFY_NEW_CLIENT_ASSIGNMENT: "info",
  NOTIFY_PAYMENT_RECEIVED: "success",
  NOTIFY_LOAN_DISBURSEMENT: "success",
  NOTIFY_LOAN_CLOSURE: "success",
  NOTIFY_PTP_REMINDER: "warning",
  NOTIFY_PTP_EXPIRY: "warning",
  NOTIFY_PRE_DUE_REMINDER: "warning",
  NOTIFY_OVERDUE_NOTICE: "warning",
  NOTIFY_CATEGORY_ESCALATION: "danger",
  NOTIFY_MANUAL_PAYMENT_VERIFIED: "success",
  NOTIFY_MANUAL_PAYMENT_REJECTED: "danger",
  NOTIFY_MICRO_TIER_PROMOTION: "success",
  NOTIFY_MICRO_TIER_DEMOTION: "warning",
  NOTIFY_MICRO_LOAN_REJECTION: "warning",
}

function labelForKey(key?: string | null, fallback?: string | null) {
  if (key && KEY_LABELS[key]) {
    return KEY_LABELS[key]
  }

  if (key) {
    return getTriggerDisplayName(key)
  }

  if (fallback) {
    return getTriggerDisplayName(fallback)
  }

  return "System update"
}

function toneForKey(key?: string | null, fallback?: string | null): NotificationTone {
  if (key && TONE_BY_KEY[key]) {
    return TONE_BY_KEY[key]
  }

  if (fallback && TONE_BY_KEY[fallback]) {
    return TONE_BY_KEY[fallback]
  }

  return "info"
}

function targetForResource(
  resourceType: string | null,
  resourceId: string | null,
): AdminNotificationTarget | null {
  switch (resourceType) {
    case "tickets":
      return {
        label: resourceId ? "Opens linked ticket" : "Opens tickets workspace",
        path: resourceId ? `/tickets?selected_ticket_id=${encodeURIComponent(resourceId)}` : "/tickets",
      }
    case "clients":
      return {
        label: resourceId ? "Opens linked client" : "Opens client portfolio",
        path: resourceId ? `/clients?selected_client_id=${encodeURIComponent(resourceId)}` : "/clients",
      }
    case "disbursements":
      return {
        label: resourceId ? "Opens linked disbursement" : "Opens disbursement queue",
        path: resourceId
          ? `/disbursements?selected_disbursement_id=${encodeURIComponent(resourceId)}`
          : "/disbursements",
      }
    case "targets":
      return {
        label: "Opens target performance",
        path: "/targets",
      }
    case "dashboard":
      return {
        label: "Opens dashboard summary",
        path: "/dashboard",
      }
    case "system-health":
    case "backup_logs":
      return {
        label: "Opens system health",
        path: "/system-health",
      }
    case "loans":
      return {
        label: resourceId ? "Searches linked loan" : "Opens client portfolio",
        path: resourceId ? `/clients?search=${encodeURIComponent(resourceId)}` : "/clients",
      }
    case "report_runs":
      return {
        label: "Opens reports",
        path: "/reports",
      }
    default:
      return null
  }
}

export function getAdminNotificationPresentation(
  item: Pick<
    AdminNotificationRow,
    "alertKey" | "notificationKey" | "eventType" | "resourceType" | "resourceId"
  >,
): AdminNotificationPresentation {
  const primaryKey = item.alertKey ?? item.notificationKey

  return {
    categoryLabel: item.alertKey ? "System alert" : "Workflow notification",
    eventLabel: labelForKey(primaryKey, item.eventType),
    eventTone: toneForKey(primaryKey, item.eventType),
    target: targetForResource(item.resourceType, item.resourceId),
  }
}
